/**
 * apps/ats-spa client for /v2/agent endpoints.
 *
 * Mirrors the shape of chats.api.ts but stripped to the Stage 1 surface:
 * list, create, get, getMessages (hydration), getActiveRun (poll),
 * archive, and sendAgentMessage (SSE).
 *
 * The SSE event vocabulary mirrors apps/agent's server.ts:
 *   stream_event   — Anthropic SDK RawMessageStreamEvent
 *   message        — full Message envelope (user OR assistant)
 *   done           — terminal
 *   error          — terminal
 */

import api from '@/services/api';
import { getAccessToken, setAccessToken } from '@/lib/session';
import { getServerUrl } from '@/lib/environment';

// http.ts already prefixes /api on the baseURL, so paths here start with /v2.
const BASE = '/v2/agent/chats';

export type AgentChatStatus = 'active' | 'archived';

export type AgentMessageRole = 'user' | 'assistant';

export interface AgentChat {
    _id: string;
    organizationId: string;
    userId: string;
    title: string;
    status: AgentChatStatus;
    /** Stage 2 (job-manage): job binding for 'general' chats. */
    jobId?: string | null;
    lastMessageAt: string | null;
    createdAt: string;
    updatedAt: string;
}

export type SearchV3ListState =
    | 'idle'
    | 'calibrating'
    | 'daily_active'
    | 'attention_needed'
    | 'paused'
    | 'archived';

export type SearchV3SourcedReviewStatus =
    | 'pending_review'
    | 'shortlist'
    | 'reject';

/**
 * Sourced shortlist item returned by the autosource endpoints. Kept loose —
 * the renderer owns the full shape — but the outreach-membership flag is
 * declared here since the server computes it by identity.
 */
export type SearchV3AutoSourceShortlistItem = {
    inOutreach?: boolean;
    [key: string]: unknown;
};

export interface SearchV3ListItem {
    chatId: string;
    artifactId?: string;
    title: string;
    description?: string;
    roleLabel: string;
    state: SearchV3ListState;
    autoSource: {
        enabled: boolean;
        scheduleHour?: number | null;
        timezone?: string | null;
        dailyTarget?: number | null;
    };
    pendingReviewCount: number;
    sourced7d: number[];
    rankedPoolCount: number;
    sourcedCount: number;
    canDelete: boolean;
    updatedAt?: string;
}

export interface SearchV3ListSummary {
    activeAgents: number;
    pendingReview: number;
    newToday: number;
    sourcedCandidates: number;
}

export interface SearchV3ListResponse {
    results: SearchV3ListItem[];
    summary: SearchV3ListSummary;
}

export type SearchV3AutoSourceOpsHealth = 'ok' | 'warning' | 'not_observed';

export interface SearchV3AutoSourceIdentity {
    id: string | null;
    name?: string | null;
    email?: string | null;
}

export interface SearchV3AutoSourceChatRef {
    id: string | null;
    title: string;
    status?: string | null;
}

export interface SearchV3AutoSourceTick {
    id: string | null;
    workerKey: string | null;
    status: string | null;
    workerPool: string | null;
    startedAt: string | null;
    completedAt: string | null;
    durationMs: number;
    dueCutoffAt: string | null;
    maxConfigs: number;
    activeWaitMs: number;
    activePollMs: number;
    scannedCount: number;
    queuedCount: number;
    skippedActiveCount: number;
    skippedClaimedElsewhereCount: number;
    failedCount: number;
    errorMessage: string | null;
    eventCount: number;
    events: Array<{
        configId: string | null;
        chatId: string | null;
        organizationId: string | null;
        status: string | null;
        workerPool: string | null;
        sourcingRunId: string | null;
        agentRunId: string | null;
        scheduledFor: string | null;
        queuedAt: string | null;
        completedAt: string | null;
        waitedMs: number;
        errorMessage: string | null;
    }>;
}

export interface SearchV3AutoSourceConfigOps {
    id: string | null;
    name: string;
    status: string | null;
    dailyTarget: number;
    cadence: string | null;
    scheduleHour: number | null;
    timezone: string | null;
    organization: SearchV3AutoSourceIdentity;
    user: SearchV3AutoSourceIdentity | null;
    chat: SearchV3AutoSourceChatRef | null;
    lastRunAt: string | null;
    nextRunAt: string | null;
    runNowAllowedUntil: string | null;
    updatedAt: string | null;
    scheduler: {
        status: string | null;
        leaseOwner: string | null;
        leaseUntil: string | null;
        lastTickAt: string | null;
        lastClaimedAt: string | null;
        lastScheduledFor: string | null;
        lastQueuedAt: string | null;
        lastQueuedRunId: string | null;
        lastQueuedAgentRunId: string | null;
        lastSkippedAt: string | null;
        lastActiveRunId: string | null;
        lastActiveRunKind: string | null;
        lastWaitedMs: number | null;
        lastCompletedTickAt: string | null;
        lastFailedAt: string | null;
        lastErrorMessage: string | null;
    };
    flags: {
        due: boolean;
        overdue: boolean;
        leased: boolean;
        attention: boolean;
    };
}

export interface SearchV3AutoSourceRunOps {
    id: string | null;
    sourcingConfigId: string | null;
    agentRunId: string | null;
    status: string | null;
    targetCount: number;
    scannedCount: number;
    collectedCount: number;
    scoredCount: number;
    deliveredCount: number;
    bufferUsedCount: number;
    bufferedCount: number;
    outOfLeadsTrigger: string | null;
    exhaustionReason: string | null;
    diagnosisMessage: string | null;
    errorMessage: string | null;
    startedAt: string | null;
    endedAt: string | null;
    createdAt: string | null;
    updatedAt: string | null;
    scheduler: {
        source: string | null;
        trigger: string | null;
        workerKey: string | null;
        tickId: string | null;
        scheduledFor: string | null;
        queuedAt: string | null;
    };
    organization: SearchV3AutoSourceIdentity;
    user: SearchV3AutoSourceIdentity | null;
    chat: SearchV3AutoSourceChatRef | null;
    config: {
        id: string | null;
        name: string | null;
        status: string | null;
        nextRunAt: string | null;
        schedulerStatus: string | null;
    } | null;
    agentRun: {
        id: string | null;
        status: string | null;
        workerPool: string | null;
        leaseOwner: string | null;
        leaseUntil: string | null;
        heartbeatAt: string | null;
        attempts: number;
    } | null;
    progress: {
        type: string | null;
        message: string | null;
        at: string | null;
        seq: number | null;
    } | null;
    flags: {
        stuck: boolean;
        reason: string | null;
    };
}

export interface SearchV3AutoSourceWorkerRunOps {
    id: string | null;
    status: string | null;
    agentType: string | null;
    workerPool: string | null;
    leaseOwner: string | null;
    leaseUntil: string | null;
    heartbeatAt: string | null;
    attempts: number;
    startedAt: string | null;
    endedAt: string | null;
    createdAt: string | null;
    updatedAt: string | null;
    errorMessage: string | null;
    organization: SearchV3AutoSourceIdentity;
    user: SearchV3AutoSourceIdentity | null;
    chat: SearchV3AutoSourceChatRef | null;
    sourcingConfigId: string | null;
    sourcingRunId: string | null;
    trigger: string | null;
    progress: {
        type: string | null;
        message: string | null;
        at: string | null;
        seq: number | null;
    } | null;
    flags: {
        stuck: boolean;
        reason: string | null;
    };
}

export interface SearchV3AutoSourceOpsResponse {
    generatedAt: string;
    thresholds: {
        staleTickMs: number;
        stuckQueuedMs: number;
        stuckRunningMs: number;
        recentWindowMs: number;
    };
    scheduler: {
        observed: boolean;
        latestTickAgeMs: number | null;
        latestTick: SearchV3AutoSourceTick | null;
        recentTicks: SearchV3AutoSourceTick[];
    };
    summary: {
        health: SearchV3AutoSourceOpsHealth;
        attentionCount: number;
        configs: {
            total: number;
            active: number;
            paused: number;
            exhausted: number;
            archived: number;
            dueNow: number;
            overdue: number;
            noSchedule: number;
        };
        sourcingRuns: {
            queued: number;
            running: number;
            completed24h: number;
            failed24h: number;
            exhausted24h: number;
            cancelled24h: number;
            stuck: number;
            scannedCount: number;
            collectedCount: number;
            scoredCount: number;
            deliveredCount: number;
        };
        workerRuns: {
            queued: number;
            running: number;
            waitingForUser: number;
            failed24h: number;
            stuck: number;
            pools: Array<{ workerPool: string; status: string; count: number }>;
        };
    };
    configs: SearchV3AutoSourceConfigOps[];
    activeRuns: SearchV3AutoSourceRunOps[];
    recentRuns: SearchV3AutoSourceRunOps[];
    workerRuns: SearchV3AutoSourceWorkerRunOps[];
}

/**
 * One persisted content block. Mirrors Anthropic's ContentBlockParam
 * union, narrowed to the variants Stage 2 actually persists:
 *   - text          (assistant + user free-text)
 *   - tool_use      (assistant rounds that called a tool)
 *   - tool_result   (user rounds carrying tool outputs)
 *
 * Stage 3+ may add image / thinking blocks here.
 */
export type PersistedTextBlock = { type: 'text'; text: string };
export type PersistedToolUseBlock = {
    type: 'tool_use';
    id: string;
    name: string;
    input: Record<string, unknown>;
};
export type PersistedToolResultBlock = {
    type: 'tool_result';
    tool_use_id: string;
    content: string | PersistedTextBlock[];
    is_error?: boolean;
};
export type AttachmentKind = 'image' | 'pdf' | 'text' | 'docx' | 'xlsx';
export interface PersistedAttachmentRefBlock {
    type: 'attachment_ref';
    attachmentId: string;
    filename: string;
    mediaType: string;
    sizeBytes: number;
    kind: AttachmentKind;
}
export type PersistedContentBlock =
    | PersistedTextBlock
    | PersistedToolUseBlock
    | PersistedToolResultBlock
    | PersistedAttachmentRefBlock;

export interface PersistedAgentMessage {
    role: AgentMessageRole;
    /** String for legacy + plain chat turns; ContentBlock[] for Stage-2 tool turns. */
    content: string | PersistedContentBlock[];
    uuid?: string;
    ts?: string;
}

export interface AgentActiveRun {
    runId: string;
    status:
        | 'queued'
        | 'running'
        | 'waiting_for_user'
        | 'completed'
        | 'failed'
        | 'cancelled';
    startedAt: string;
    userMessage?: string;
    lastSeq?: number;
    /**
     * Phase 1 (single-turn agents) — run-level output persisted from
     * the run_output event. Refresh-hydration source for "where did
     * this run land" (e.g. navigate to the created job).
     */
    output?: Record<string, unknown> | null;
}

/**
 * Stage 4: artifact record persisted on the chat. The harness emits
 * artifact_created/updated/shown/hidden/deleted SSE events; apps/server
 * reduces them into chat.artifacts; the SPA refresh-hydrates from here.
 *
 * `type`, `content`, `refs`, `metadata` are intentionally schema-less.
 * The SPA's artifact registry (separate file) dispatches a per-type
 * renderer.
 */
export interface AgentArtifact {
    id: string;
    type: string;
    content?: unknown;
    refs?: unknown;
    metadata?: unknown;
    visible: boolean;
    createdAt: string;
    updatedAt: string;
    sourceToolUseId?: string;
}

export interface AgentArtifactPatch {
    content?: unknown;
    refs?: unknown;
    metadata?: unknown;
}

/**
 * Stage 4.5: session todo list item (TodoWrite tool). Mirrors reference's
 * TodoItem shape.
 */
export interface AgentTodoItem {
    content: string;
    activeForm: string;
    status: 'pending' | 'in_progress' | 'completed';
}

/**
 * Stage 4.7: workflow mode. Identifies which composer-replacing
 * workflow surface (if any) is currently active on the chat. The SPA's
 * workflow registry dispatches Composer + panel-renderer choices on
 * `kind`. `kind === 'default'` means free-form chat (default composer).
 *
 * The harness mirrors this shape on AppState.workflowMode and emits
 * `workflow_mode_changed` events when tools mutate it. Persisted on
 * the chat document so refresh restores the surface.
 */
export interface AgentWorkflowMode {
    kind: string;
    artifactId?: string;
}

export type TalentSearchVerdict = 'kept' | 'maybe' | 'rejected' | 'pending';

export interface TalentSearchRefinementAction {
    candidateId: string;
    candidateName?: string;
    status: TalentSearchVerdict;
    reason?: string;
}

export interface TalentSearchRefinementContext {
    kind: 'talent_search_refinement';
    artifactId: string;
    round?: number;
    visibleFeedback?: string;
    actions: TalentSearchRefinementAction[];
}

export type AgentWorkflowContext = TalentSearchRefinementContext;

/**
 * Stage 2.2 (job-manage) — workspace state sent with each message so
 * the agent knows what the user is looking at and which tab ids the
 * Workspace tool may target. Server sanitizes and bakes it into the
 * system prompt.
 */
export interface AgentUiContextTab {
    id: string;
    label: string;
    state: 'active' | 'open' | 'closed' | 'addable';
}

export interface AgentUiContext {
    view: 'workspace' | 'kanban';
    activeTab: string | null;
    tabs: AgentUiContextTab[];
}

/**
 * Stage 2.2 — imperative workspace command from a tool (`ui_command`
 * SSE event). The hosting surface (manage page) applies it; other
 * surfaces ignore it.
 */
export interface AgentUiCommand {
    // 'navigate' (Stage 2.5) targets an app ROUTE (create_job_role →
    // /v2/jobs/<id>/manage) instead of a tab id.
    command: 'open_tab' | 'activate_tab' | 'set_view' | 'navigate';
    target?: string;
    view?: 'workspace' | 'kanban';
}

export interface AgentSendOptions {
    workflowContext?: AgentWorkflowContext;
    /** Stage 2.2 (job-manage): current workspace state. */
    uiContext?: AgentUiContext;
    /**
     * Attachments uploaded via /attachments/upload-url + /finalize.
     * Rich metadata is carried so the store can render chips on the
     * optimistic user bubble; only `attachmentId` is sent over the
     * wire to apps/server (the rest is already on the server doc).
     */
    attachments?: AgentAttachment[];
}

// ─── File attachments (Claude Code-shaped) ───────────────────────────

export interface AgentAttachment {
    attachmentId: string;
    filename: string;
    mediaType: string;
    sizeBytes: number;
    kind: AttachmentKind;
    status: 'pending' | 'ready' | 'deleted';
    extractionStatus?: 'none' | 'pending' | 'ok' | 'failed';
    extractionError?: string | null;
    thumbnailDataUrl?: string | null;
}

export const SUPPORTED_ATTACHMENT_MEDIA_TYPES: Record<
    AttachmentKind,
    string[]
> = {
    image: ['image/png', 'image/jpeg', 'image/gif', 'image/webp'],
    pdf: ['application/pdf'],
    text: [
        'text/plain',
        'text/markdown',
        'text/csv',
        'application/json',
        'application/xml',
    ],
    docx: [
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ],
    xlsx: ['application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'],
};

export const ATTACHMENT_MAX_BYTES: Record<AttachmentKind, number> = {
    image: 5 * 1024 * 1024,
    pdf: 20 * 1024 * 1024,
    text: 1 * 1024 * 1024,
    docx: 10 * 1024 * 1024,
    xlsx: 10 * 1024 * 1024,
};

export const PER_TURN_ATTACHMENT_LIMIT = 10;

export function classifyFileMediaType(
    mediaType: string,
): AttachmentKind | null {
    const lower = (mediaType || '').toLowerCase();
    for (const [kind, types] of Object.entries(
        SUPPORTED_ATTACHMENT_MEDIA_TYPES,
    ) as [AttachmentKind, string[]][]) {
        if (types.includes(lower)) return kind;
    }
    if (lower.startsWith('text/')) return 'text';
    return null;
}

/**
 * Two-step upload:
 *   1. POST /attachments/upload-url → { attachment, uploadUrl }
 *   2. PUT bytes directly to S3 via uploadUrl
 *   3. POST /attachments/:id/finalize → confirms + runs extraction
 *
 * Returns the finalized attachment row on success.
 */
export async function uploadAgentAttachment({
    chatId,
    file,
    onProgress,
}: {
    chatId: string;
    file: File;
    onProgress?: (
        phase: 'minting' | 'uploading' | 'finalizing' | 'done',
    ) => void;
}): Promise<AgentAttachment> {
    onProgress?.('minting');
    const kind = classifyFileMediaType(file.type);
    if (!kind) {
        throw new Error(
            `Unsupported file type "${file.type || 'unknown'}". Try a PDF, image, .docx, .xlsx, or plain text.`,
        );
    }
    if (file.size > ATTACHMENT_MAX_BYTES[kind]) {
        const mb = Math.floor(ATTACHMENT_MAX_BYTES[kind] / (1024 * 1024));
        throw new Error(`File is too large — max ${mb} MB for ${kind} files.`);
    }

    const mint = await api.post<{
        attachment: AgentAttachment;
        uploadUrl: string;
    }>(`${BASE}/${chatId}/attachments/upload-url`, {
        filename: file.name,
        mediaType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
    });

    onProgress?.('uploading');
    const putRes = await fetch(mint.data.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
    });
    if (!putRes.ok) {
        throw new Error(
            `Upload failed (${putRes.status}). The signed link may have expired — try again.`,
        );
    }

    onProgress?.('finalizing');
    const finalize = await api.post<{ attachment: AgentAttachment }>(
        `${BASE}/${chatId}/attachments/${mint.data.attachment.attachmentId}/finalize`,
        {},
    );

    onProgress?.('done');
    return finalize.data.attachment;
}

/**
 * Short-lived inline GET URL — used by message-bubble chips to open the
 * file in a new tab. Worker has its own S3 IAM; this is for UI only.
 */
export async function getAttachmentDownloadUrl({
    chatId,
    attachmentId,
    inline = true,
}: {
    chatId: string;
    attachmentId: string;
    inline?: boolean;
}): Promise<{ url: string; filename: string; mediaType: string }> {
    const { data } = await api.get<{
        url: string;
        filename: string;
        mediaType: string;
    }>(`${BASE}/${chatId}/attachments/${attachmentId}/download-url`, {
        params: { inline: inline ? '1' : '0' },
    });
    return data;
}

// ─── Stage 5b: permission rail ───────────────────────────────────────

/**
 * Decision the user clicks in the overlay. `allow_remember` is the
 * "Allow + remember" path; Stage 5c reads it server-side and writes
 * a persistent rule. In 5b it's treated identically to `allow`.
 */
export type PermissionDecision = 'allow' | 'allow_remember' | 'deny';

/**
 * Open permission request — what the SPA renders in the overlay.
 * Mirrors the harness's PermissionRequestEvent (minus the discriminator
 * field; here it's always-implied by the slot it lives in).
 */
export interface AgentPermissionRequest {
    toolUseId: string;
    toolName: string;
    userFacingName: string;
    input: Record<string, unknown>;
    message?: string;
    requestedAt: string | null;
}

/**
 * Returned by /messages hydration when an active run has open
 * permission prompts. The SPA mounts the overlay if this is non-null,
 * keyed by `runId` (needed for the response POST).
 */
export interface AgentPendingPermissions {
    runId: string;
    requests: AgentPermissionRequest[];
}

// ─── Stage 5c: persistent permission rules ───────────────────────────

/**
 * A saved permission rule. Mirrors the harness's `PermissionRule`
 * (see apps/agent/src/policy/permissions.ts) and the
 * server's stored shape on `User.agentPermissionRules` /
 * `AgentChat.sessionPermissionRules`.
 */
export interface AgentPermissionRule {
    id: string;
    /**
     * Stage 5.5 — three behaviors mirroring reference:
     *   - `'allow'`: auto-allow without prompting
     *   - `'deny'`: auto-deny without prompting
     *   - `'ask'`: force the tool to ask even when it would allow
     *     (no UI creates these today; Stage 6+ Bash will)
     */
    behavior: 'allow' | 'deny' | 'ask';
    toolName: string;
    /** '*' = any input for this tool (the default for the SPA's overlay). */
    ruleContent: string;
    scope: 'session' | 'user';
    /** ISO timestamp. */
    createdAt: string;
    note?: string;
}

/**
 * Both rule scopes for the current (chat, user). `userRules` survive
 * across all chats for this user; `sessionRules` live for the
 * lifetime of this chat only.
 */
export interface AgentPermissionRulesResponse {
    userRules: AgentPermissionRule[];
    sessionRules: AgentPermissionRule[];
}

// ─── Stage 5f: permission mode ────────────────────────────────────────

/**
 * User-chosen permission posture for a chat. Mirrors a subset of the
 * harness's `PermissionMode` — only the modes we actually surface in
 * the SPA. Plan + auto + (future) tier modes stay reference-faithful
 * seats in the harness but unsurfaced.
 */
export type AgentPermissionMode =
    | 'default' // prompt for sensitive tools
    | 'acceptEdits' // auto-allow read-only tools
    | 'dontAsk' // any 'ask' becomes 'deny' (no prompts)
    | 'bypassPermissions'; // allow everything (dev mode)

export interface AgentMessagesResponse {
    chatId: string;
    title: string;
    status: AgentChatStatus;
    agentType?: string;
    messages: PersistedAgentMessage[];
    artifacts: AgentArtifact[];
    todos: AgentTodoItem[];
    workflowMode: AgentWorkflowMode;
    chatCreatedAt: string | null;
    lastMessageAt: string | null;
    activeRun: AgentActiveRun | null;
    latestRun:
        | (AgentActiveRun & {
              endedAt?: string | null;
              errorMessage?: string | null;
          })
        | null;
    /**
     * Stage 5b: open permission prompts for the active run, if any.
     * Null when nothing is pending (most chats most of the time).
     */
    pendingPermissions: AgentPendingPermissions | null;
    /**
     * Stage 5c: saved rules visible to this chat — both user-scoped
     * (cross-chat) and session-scoped (this chat only). The SPA renders
     * these in the "Saved approvals" header menu.
     */
    permissionRules: AgentPermissionRulesResponse;
    /**
     * Stage 5f: user-chosen permission posture for this chat. The
     * SPA's ModeSelector reflects + mutates this. The next /run
     * picks up the value; in-flight runs keep their seeded mode.
     */
    permissionMode: AgentPermissionMode;
}

export interface ListAgentChatsResponse {
    results: AgentChat[];
    total?: number;
    page: number;
    limit: number;
}

/**
 * Anthropic SDK stream events + harness-emitted envelopes forwarded by
 * apps/agent.
 *   stream_event              — Anthropic RawMessageStreamEvent
 *   message                   — full Message envelope (user/assistant/tool_result)
 *   tool_call_started         — { id, name, input } when a tool starts (Stage 2)
 *   tool_call_completed       — { id, name, status, summary } when a tool returns (Stage 2)
 *   done                      — terminal
 *   error                     — terminal
 */
export type AgentSSEEventType =
    | 'stream_event'
    | 'message'
    | 'tool_call_started'
    | 'tool_call_progress'
    | 'tool_call_completed'
    | 'api_error_retry'
    | 'artifact_created'
    | 'artifact_updated'
    | 'artifact_shown'
    | 'artifact_hidden'
    | 'artifact_deleted'
    | 'todos_updated'
    | 'workflow_mode_changed'
    | 'permission_requested'
    | 'permission_resolved'
    | 'tool_permission_auto_allowed'
    // Phase 1 (single-turn agents): run-level output from a terminal
    // tool — { output: { resourceType, resourceId, route, … } }.
    // Single-turn surfaces navigate on it; the chat page's router
    // ignores it via its unknown-type fallthrough.
    | 'run_output'
    // Phase 3 (bound agents): an ATS-data mutation a tool performed —
    // { collection, id, op }. Embedded surfaces (job-edit dock)
    // refetch the named resource; the chat page ignores it.
    | 'data_changed'
    // Stage 2.2 (job-manage): an imperative workspace command from a
    // tool — { command, target?, view? }. The manage page applies it;
    // other surfaces ignore it.
    | 'ui_command'
    | 'user_signal_received'
    | 'run_cancel_requested'
    | 'run_cancelled'
    | 'done'
    | 'error';

export interface AgentSSEEvent {
    event: AgentSSEEventType;
    data: Record<string, unknown>;
    seq?: number;
    replayed?: boolean;
}

export interface AgentRunEventRecord {
    seq: number;
    type: AgentSSEEventType;
    payload: Record<string, unknown>;
    createdAt?: string | null;
}

function normalizeStreamErrorMessage(message: unknown) {
    const text = String(message || '').trim();
    if (
        /fetch failed|failed to fetch|network\s*error|networkerror|load failed|err_network/i.test(
            text,
        )
    ) {
        return 'Connection to the search agent was interrupted. Please retry; if it keeps happening, check that the agent service is running.';
    }
    return text || 'Agent stream failed.';
}

function isPreviousRunStoppingConflict(status: number, message: unknown) {
    return (
        status === 409 &&
        /previous run is still stopping/i.test(String(message || ''))
    );
}

function waitForRetry(ms: number, signal: AbortSignal) {
    return new Promise<void>((resolve) => {
        if (signal.aborted) {
            resolve();
            return;
        }
        const timeout = window.setTimeout(cleanup, ms);
        function cleanup() {
            window.clearTimeout(timeout);
            signal.removeEventListener('abort', cleanup);
            resolve();
        }
        signal.addEventListener('abort', cleanup, { once: true });
    });
}

/**
 * Refresh the access token via the axios layer (so the new token gets
 * persisted via setAccessToken + the rest of the app benefits). Returns
 * the new token on success, or null if refresh failed.
 *
 * Used to bridge the SSE-fetch path into the same auto-refresh flow
 * that http.ts's axios interceptor provides for everything else. The
 * SSE endpoint uses raw fetch (not axios) so it has to refresh
 * manually on 401.
 */
async function refreshAccessTokenForStream(): Promise<string | null> {
    try {
        const response = await api.post<{ accessToken: string }>(
            '/auth/refresh',
        );
        const token = response.data?.accessToken;
        if (typeof token === 'string' && token.length > 0) {
            setAccessToken(token);
            return token;
        }
        return null;
    } catch {
        return null;
    }
}

/**
 * Open the SSE POST to /messages. Returns a 2xx Response ready to
 * stream, or throws. On 401 attempts a single token refresh and one
 * retry — mirroring the axios interceptor's behaviour. Other non-2xx
 * statuses bubble up as a stream-error so the caller can surface them
 * via `onEvent({event: 'error', ...})`.
 */
async function openMessageStream(
    chatId: string,
    message: string,
    signal: AbortSignal,
    options?: AgentSendOptions,
): Promise<Response> {
    const fire = async (token: string | null) =>
        fetch(`${getServerUrl()}${BASE}/${chatId}/messages`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({
                message,
                ...(options?.workflowContext
                    ? { workflowContext: options.workflowContext }
                    : {}),
                ...(options?.uiContext ? { uiContext: options.uiContext } : {}),
                ...(options?.attachments && options.attachments.length > 0
                    ? {
                          attachments: options.attachments.map((a) => ({
                              attachmentId: a.attachmentId,
                          })),
                      }
                    : {}),
            }),
            signal,
            credentials: 'include',
        });

    let response = await fire(getAccessToken());

    if (response.status === 401) {
        // Drain the body so we don't leak the connection.
        try {
            await response.body?.cancel();
        } catch {
            /* ignore */
        }
        const fresh = await refreshAccessTokenForStream();
        if (!fresh) {
            // Refresh itself failed — let http.ts's redirectToLogin
            // handle session_expired on the next axios call. Surface
            // a structured error for now.
            throw new Error('Session expired. Please log in again.');
        }
        response = await fire(fresh);
    }

    return response;
}

async function openRunEventsStream(
    chatId: string,
    runId: string,
    afterSeq: number,
    signal: AbortSignal,
): Promise<Response> {
    const fire = async (token: string | null) =>
        fetch(
            `${getServerUrl()}${BASE}/${chatId}/runs/${runId}/events?afterSeq=${encodeURIComponent(
                String(Math.max(0, Math.floor(afterSeq || 0))),
            )}`,
            {
                method: 'GET',
                headers: {
                    ...(token ? { Authorization: `Bearer ${token}` } : {}),
                },
                signal,
                credentials: 'include',
            },
        );

    let response = await fire(getAccessToken());

    if (response.status === 401) {
        try {
            await response.body?.cancel();
        } catch {
            /* ignore */
        }
        const fresh = await refreshAccessTokenForStream();
        if (!fresh) {
            throw new Error('Session expired. Please log in again.');
        }
        response = await fire(fresh);
    }

    return response;
}

function toRecord(value: unknown): Record<string, unknown> {
    if (value && typeof value === 'object' && !Array.isArray(value)) {
        return value as Record<string, unknown>;
    }
    return { value };
}

function parseSseSeq(id: string): number | undefined {
    const seq = Number(id);
    return Number.isFinite(seq) && seq > 0 ? seq : undefined;
}

async function readSseFrames(
    response: Response,
    onFrame: (frame: {
        event: string;
        data: Record<string, unknown>;
        id?: string;
    }) => void,
) {
    const reader = response.body?.getReader();
    if (!reader) return;

    const decoder = new TextDecoder();
    let buffer = '';
    let currentEvent = '';
    let currentId = '';

    while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
            if (line.startsWith('id: ')) {
                currentId = line.slice(4).trim();
            } else if (line.startsWith('event: ')) {
                currentEvent = line.slice(7).trim();
            } else if (line.startsWith('data: ') && currentEvent) {
                try {
                    const data = toRecord(JSON.parse(line.slice(6)));
                    onFrame({
                        event: currentEvent,
                        data,
                        ...(currentId ? { id: currentId } : {}),
                    });
                } catch {
                    // skip malformed
                }
                currentEvent = '';
                currentId = '';
            }
        }
    }
}

export const agentApi = {
    async list(
        params: {
            page?: number;
            limit?: number;
            includeArchived?: boolean;
            includeTotal?: boolean;
            /**
             * Phase 1 (agent registry): scope the list to one agentType.
             * Omitted → server defaults to 'search' (back-compat). Pass
             * a specific type (e.g. 'search_v2') to isolate a surface's
             * own chats; pass 'all' to disable the filter.
             */
            agentType?: string;
        } = {},
    ) {
        const { data } = await api.get<ListAgentChatsResponse>(BASE, {
            params,
        });
        return data;
    },
    async get(chatId: string) {
        const { data } = await api.get<{ chat: AgentChat }>(
            `${BASE}/${chatId}`,
        );
        return data.chat;
    },
    /**
     * Phase 1 (agent registry): `agentType` selects which agent owns
     * the chat ('search' default; 'job_create' for the single-turn
     * job surface). `context` is the resource binding for bound
     * agents — server-validated at creation; unknown agentType → 400.
     */
    async create(
        body: {
            agentType?: string;
            context?: Record<string, unknown> | null;
            /**
             * Stage 2 (job-manage): bind a 'general' chat to a job at
             * creation. Server-validated (ownership); 400 for other
             * agent types.
             */
            jobId?: string;
        } = {},
    ) {
        const { data } = await api.post<{ chat: AgentChat }>(BASE, body);
        return data.chat;
    },
    /**
     * Stage 2 (job-manage): the manage page's chat switcher — the
     * caller's chats bound to one job, most recent first.
     */
    async listChatsByJob(
        jobId: string,
        params: { page?: number; limit?: number; includeArchived?: boolean } = {},
    ) {
        const { data } = await api.get<ListAgentChatsResponse>(
            `${BASE}/by-job/${jobId}`,
            { params },
        );
        return data;
    },
    /**
     * Phase 3 (bound agents): find-or-create the caller's chat for
     * (agentType, resource) — embedded surfaces reopen the same
     * conversation across visits.
     */
    async ensureChat(body: {
        agentType: string;
        context: { resourceType: string; resourceId: string };
    }) {
        const { data } = await api.post<{ chat: AgentChat; created: boolean }>(
            `${BASE}/ensure`,
            body,
        );
        return data;
    },
    async archive(chatId: string) {
        const { data } = await api.delete<{ ok: boolean }>(`${BASE}/${chatId}`);
        return data;
    },
    async unarchive(chatId: string) {
        const { data } = await api.post<{ ok: boolean }>(
            `${BASE}/${chatId}/unarchive`,
            {},
        );
        return data;
    },
    async deleteIdleSearchV3(chatId: string) {
        const { data } = await api.delete<{ ok: boolean }>(
            `${BASE}/${chatId}/permanent`,
        );
        return data;
    },
    async listSearchV3Summaries(params: {
        includeArchived?: boolean;
        limit?: number;
    } = {}) {
        const { data } = await api.get<SearchV3ListResponse>(
            '/v2/agent/search-v3/summaries',
            { params },
        );
        return data;
    },
    async getSearchV3AutoSourceOps(params: { limit?: number } = {}) {
        const { data } = await api.get<SearchV3AutoSourceOpsResponse>(
            '/v2/agent/search-v3/autosource/ops',
            { params },
        );
        return data;
    },
    async getMessages(chatId: string) {
        const { data } = await api.get<AgentMessagesResponse>(
            `${BASE}/${chatId}/messages`,
        );
        return data;
    },
    async getActiveRun(chatId: string) {
        const { data } = await api.get<{ activeRun: AgentActiveRun | null }>(
            `${BASE}/${chatId}/runs/active`,
        );
        return data;
    },
    /**
     * Stage 1.6: cluster-correct Stop. Flags any in-flight runs on
     * this chat for cancellation server-side. The streaming pod polls
     * Mongo and aborts its upstream fetch when it sees the flag.
     * Returns the count of runs that were flagged (0 if nothing
     * active — the call is idempotent).
     */
    async stopActive(chatId: string) {
        const { data } = await api.post<{ ok: boolean; cancelled: number }>(
            `${BASE}/${chatId}/stop`,
            {},
        );
        return data;
    },
    /**
     * search_v2 "Re-evaluate glimpse": re-score the search_strategy
     * artifact's already-collected sample against an edited criteria
     * list. No agent turn — apps/server ships the sample to the worker's
     * Gemini scorer, persists the verdicts in place, and returns the
     * updated artifact content for the SPA to apply to its store.
     */
    async reevaluateSearchStrategy(args: {
        chatId: string;
        artifactId: string;
        criteria: string[];
    }) {
        const { chatId, ...body } = args;
        const { data } = await api.post<{
            artifactId: string;
            content: unknown;
        }>(`${BASE}/${chatId}/search-strategy/reevaluate`, body);
        return data;
    },
    /**
     * search_v3 transparency rail: toggle one structured filter hard↔soft,
     * recompile + preview in the worker, and return updated artifact content.
     */
    async repreviewSearchV3(args: {
        chatId: string;
        artifactId: string;
        filterId: string;
        tier: 'hard' | 'soft';
    }) {
        const { chatId, ...body } = args;
        const { data } = await api.post<{
            artifactId: string;
            content: unknown;
        }>(`${BASE}/${chatId}/search-v3/repreview`, body);
        return data;
    },
    async updateSearchV3Metadata(args: {
        chatId: string;
        artifactId: string;
        title?: string;
        description?: string;
    }) {
        const { chatId, ...body } = args;
        const { data } = await api.patch<{
            artifactId: string;
            content: unknown;
            metadata: unknown;
        }>(`${BASE}/${chatId}/search-v3/meta`, body);
        return data;
    },
    async getSearchV3Calibration(args: { chatId: string; artifactId: string }) {
        const { chatId, artifactId } = args;
        const { data } = await api.get<{
            accepted: unknown[];
            rejected: unknown[];
            acceptedCount: number;
            rejectedCount: number;
            autoSourceRecommended: boolean;
        }>(`${BASE}/${chatId}/search-v3/calibration`, {
            params: { artifactId },
        });
        return data;
    },
    async submitSearchV3Feedback(args: {
        chatId: string;
        artifactId: string;
        message?: string;
        actions: Array<Record<string, unknown>>;
    }) {
        const { chatId, ...body } = args;
        const { data } = await api.post<{
            accepted: unknown[];
            rejected: unknown[];
            acceptedCount: number;
            rejectedCount: number;
            autoSourceRecommended: boolean;
            feedback?: unknown;
        }>(`${BASE}/${chatId}/search-v3/feedback`, body);
        return data;
    },
    async removeSearchV3Feedback(args: {
        chatId: string;
        artifactId: string;
        candidateKey: string;
    }) {
        const { chatId, ...body } = args;
        const { data } = await api.delete<{
            accepted: unknown[];
            rejected: unknown[];
            acceptedCount: number;
            rejectedCount: number;
            autoSourceRecommended: boolean;
            feedback?: unknown;
        }>(`${BASE}/${chatId}/search-v3/feedback`, {
            data: body,
        });
        return data;
    },
    async getSearchV3AutoSource(args: {
        chatId: string;
        artifactId: string;
        limit?: number;
    }) {
        const { chatId, artifactId, limit } = args;
        const { data } = await api.get<{
            config: unknown | null;
            runs: unknown[];
            shortlist: SearchV3AutoSourceShortlistItem[];
        }>(`${BASE}/${chatId}/search-v3/autosource`, {
            params: { artifactId, limit },
        });
        return data;
    },
    async startSearchV3AutoSource(args: {
        chatId: string;
        artifactId: string;
        dailyTarget: number;
        scheduleHour?: number;
        timezone?: string;
        limit?: number;
    }) {
        const { chatId, ...body } = args;
        const { data } = await api.post<{
            config: unknown | null;
            runs: unknown[];
            shortlist: SearchV3AutoSourceShortlistItem[];
        }>(`${BASE}/${chatId}/search-v3/autosource`, body);
        return data;
    },
    async runSearchV3AutoSourceOnce(args: {
        chatId: string;
        configId: string;
        limit?: number;
        message?: string;
        trigger?: 'autosource_start' | 'autosource_manual';
    }) {
        const { chatId, configId, limit, message, trigger } = args;
        const { data } = await api.post<{
            config: unknown | null;
            runs: unknown[];
            shortlist: SearchV3AutoSourceShortlistItem[];
            run?: {
                runId: string;
                status?: string;
                userMessage?: string;
            };
            message?: string;
        }>(`${BASE}/${chatId}/search-v3/autosource/${configId}/run`, {
            limit,
            message,
            trigger,
        });
        return data;
    },
    async pauseSearchV3AutoSource(args: {
        chatId: string;
        configId: string;
        limit?: number;
    }) {
        const { chatId, configId, limit } = args;
        const { data } = await api.patch<{
            config: unknown | null;
            runs: unknown[];
            shortlist: SearchV3AutoSourceShortlistItem[];
        }>(`${BASE}/${chatId}/search-v3/autosource/${configId}/pause`, {
            limit,
        });
        return data;
    },
    async getSearchV3SourcedCandidateProfile(args: {
        chatId: string;
        configId: string;
        candidateKey: string;
    }) {
        const { chatId, configId, candidateKey } = args;
        const { data } = await api.get<{ candidate: unknown }>(
            `${BASE}/${chatId}/search-v3/autosource/${configId}/candidates/${encodeURIComponent(
                candidateKey,
            )}/profile`,
        );
        return data;
    },
    async updateSearchV3SourcedCandidateStatus(args: {
        chatId: string;
        configId: string;
        candidateKey: string;
        reviewStatus: SearchV3SourcedReviewStatus;
    }) {
        const { chatId, configId, candidateKey, reviewStatus } = args;
        const { data } = await api.patch<{ candidate: unknown }>(
            `${BASE}/${chatId}/search-v3/autosource/${configId}/candidates/${encodeURIComponent(
                candidateKey,
            )}/status`,
            { reviewStatus },
        );
        return data;
    },
    /**
     * Stage 5b: relay the user's permission decision to the harness's
     * mailbox via apps/server. Resolves a paused tool — `allow` runs
     * it, `deny` returns an error tool_result to the model.
     *
     * Status semantics:
     *   200 — delivered (or buffered if the harness was mid-emit)
     *   410 — run already ended (we clear the pending row server-side
     *         so the overlay should dismiss on next poll/hydrate)
     *   502 — agent service unreachable
     */
    async respondToPermission(args: {
        chatId: string;
        runId: string;
        toolUseId: string;
        decision: PermissionDecision;
        scope?: 'session' | 'user';
        updatedInput?: Record<string, unknown>;
    }) {
        const { chatId, ...body } = args;
        const { data } = await api.post<{
            ok: boolean;
            delivered: boolean;
            /** Stage 5c — present when decision was 'allow_remember'. */
            savedRule?: AgentPermissionRule | null;
        }>(`${BASE}/${chatId}/permissions/respond`, body);
        return data;
    },
    /**
     * Stage 5c — list saved rules for the "Saved approvals" header
     * menu. Returns both user-scope (cross-chat) and session-scope
     * (this chat only) lists.
     */
    async listPermissionRules(chatId: string) {
        const { data } = await api.get<AgentPermissionRulesResponse>(
            `${BASE}/${chatId}/permissions/rules`,
        );
        return data;
    },
    /**
     * Stage 5c — remove a saved rule. `scope` is a hint (if known,
     * tells the server which collection to scan); omitting it makes
     * the server try both — fine for the SPA's "Forget this rule"
     * link from a tool-pill popover where the scope tag is right
     * there.
     */
    async deletePermissionRule(args: {
        chatId: string;
        ruleId: string;
        scope?: 'session' | 'user';
    }) {
        const { chatId, ruleId, scope } = args;
        const { data } = await api.delete<{ ok: boolean }>(
            `${BASE}/${chatId}/permissions/rules/${encodeURIComponent(ruleId)}`,
            { params: scope ? { scope } : undefined },
        );
        return data;
    },
    /**
     * Stage 5f — change the chat's permission posture. Persists
     * immediately; the next /run picks it up. In-flight runs keep
     * their seeded mode.
     */
    async updatePermissionMode(args: {
        chatId: string;
        mode: AgentPermissionMode;
    }) {
        const { chatId, mode } = args;
        const { data } = await api.patch<{
            ok: boolean;
            permissionMode: AgentPermissionMode;
        }>(`${BASE}/${chatId}/permission-mode`, { mode });
        return data;
    },
};

/**
 * Stream a chat turn from apps/agent (via apps/server proxy).
 *
 * Returns an AbortController so the caller can cancel mid-flight (e.g.
 * unmount or "Stop" button). Errors are surfaced through `onEvent` as
 * { event: 'error', data: { message } }.
 */
// [PERF-AUDIT] Temporary browser-side latency trace. Logs to the devtools
// console with a [PERF-AUDIT] prefix. Remove this + its call sites when done.
function perfLog(...args: unknown[]) {
    // eslint-disable-next-line no-console
    console.info('[PERF-AUDIT]', ...args);
}

export function sendAgentMessage(
    chatId: string,
    message: string,
    onEvent: (event: AgentSSEEvent) => void,
    options?: AgentSendOptions,
): AbortController {
    const controller = new AbortController();

    // [PERF-AUDIT] client-observed waterfall timestamps (ms via performance.now)
    const perfSendMs = performance.now();
    let perfTtfbMs = 0;
    let perfFirstEventMs = 0;
    let perfFirstTextMs = 0;

    (async () => {
        try {
            // Stage 4 polish: openMessageStream wraps fetch with a 401
            // auto-refresh path — mirrors the axios interceptor's behavior
            // for the raw-fetch SSE call. On hard auth failure it throws
            // and the catch below surfaces "Session expired".
            let response: Response | null = null;
            while (!controller.signal.aborted) {
                response = await openMessageStream(
                    chatId,
                    message,
                    controller.signal,
                    options,
                );

                if (response.ok) {
                    perfTtfbMs = performance.now(); // [PERF-AUDIT] headers/TTFB
                    break;
                }

                const err = await response
                    .json()
                    .catch(() => ({ error: 'Request failed' }));
                if (isPreviousRunStoppingConflict(response.status, err.error)) {
                    await waitForRetry(1000, controller.signal);
                    continue;
                }

                onEvent({
                    event: 'error',
                    data: {
                        message: normalizeStreamErrorMessage(
                            err.error || 'Request failed',
                        ),
                    },
                });
                return;
            }

            if (!response || controller.signal.aborted) {
                return;
            }

            let sawTerminalEvent = false;
            await readSseFrames(response, ({ event, data, id }) => {
                // [PERF-AUDIT] client-observed stream milestones
                const nowMs = performance.now();
                if (perfFirstEventMs === 0) {
                    perfFirstEventMs = nowMs;
                    perfLog(
                        `client: TTFB(headers)=${Math.round(perfTtfbMs - perfSendMs)}ms  ` +
                            `first SSE event=${Math.round(nowMs - perfSendMs)}ms after send`,
                    );
                }
                if (
                    perfFirstTextMs === 0 &&
                    event === 'stream_event' &&
                    (data as { type?: string })?.type ===
                        'content_block_delta' &&
                    (data as { delta?: { type?: string } })?.delta?.type ===
                        'text_delta'
                ) {
                    perfFirstTextMs = nowMs;
                    perfLog(
                        `client: first VISIBLE character=${Math.round(nowMs - perfSendMs)}ms after send`,
                    );
                }
                if (
                    event === 'done' ||
                    event === 'error' ||
                    event === 'run_cancelled'
                ) {
                    sawTerminalEvent = true;
                    perfLog(
                        `client: stream ${event} at ${Math.round(nowMs - perfSendMs)}ms after send  ` +
                            `(first char→done = ${perfFirstTextMs > 0 ? Math.round(nowMs - perfFirstTextMs) : 0}ms)`,
                    );
                }
                onEvent({
                    event: event as AgentSSEEventType,
                    data,
                    seq: id ? parseSseSeq(id) : undefined,
                });
            });
            if (!controller.signal.aborted && !sawTerminalEvent) {
                onEvent({
                    event: 'error',
                    data: {
                        message:
                            'Connection to the search agent was interrupted. Reconnecting...',
                        recoverable: true,
                    },
                });
            }
        } catch (err) {
            if ((err as Error)?.name === 'AbortError') return;
            onEvent({
                event: 'error',
                data: {
                    message: normalizeStreamErrorMessage(
                        (err as Error)?.message || 'Stream failed',
                    ),
                    recoverable: true,
                },
            });
        }
    })();

    return controller;
}

export function streamAgentRunEvents(
    chatId: string,
    runId: string,
    afterSeq: number,
    onEvent: (event: AgentSSEEvent) => void,
    options: { shouldReconnect?: () => boolean } = {},
): AbortController {
    const controller = new AbortController();
    const shouldReconnect = () =>
        options.shouldReconnect ? options.shouldReconnect() : false;

    (async () => {
        let nextAfterSeq = Math.max(0, Math.floor(afterSeq || 0));
        let retryCount = 0;

        while (!controller.signal.aborted) {
            try {
                const response = await openRunEventsStream(
                    chatId,
                    runId,
                    nextAfterSeq,
                    controller.signal,
                );

                if (!response.ok) {
                    const err = await response
                        .json()
                        .catch(() => ({ error: 'Request failed' }));
                    onEvent({
                        event: 'error',
                        data: {
                            message: normalizeStreamErrorMessage(
                                err.error || 'Request failed',
                            ),
                        },
                    });
                    return;
                }

                await readSseFrames(response, ({ event, data }) => {
                    if (event !== 'agent_event') return;
                    const seq = Number(data.seq || 0);
                    const type = data.type;
                    if (
                        !Number.isFinite(seq) ||
                        seq <= nextAfterSeq ||
                        typeof type !== 'string'
                    ) {
                        return;
                    }
                    nextAfterSeq = seq;
                    retryCount = 0;
                    onEvent({
                        event: type as AgentSSEEventType,
                        data: toRecord(data.payload),
                        seq,
                        replayed: true,
                    });
                });
            } catch (err) {
                if ((err as Error)?.name === 'AbortError') return;
                if (!shouldReconnect()) return;
                retryCount += 1;
            }

            if (controller.signal.aborted) return;
            if (!shouldReconnect()) return;
            const delayMs = Math.min(3000, 500 * Math.max(1, retryCount));
            await new Promise((resolve) => setTimeout(resolve, delayMs));
        }
    })();

    return controller;
}
